import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  GithubOutlined,
  SaveOutlined,
  LoadingOutlined,
  SecurityScanOutlined,
  LinkOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import styles from '@/styles/pages/projects/CreateProject.module.css';
import { projectsAPI } from "@/api/endpoints/projects.js";

const EditProject = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    tech_stack: [],
    repository_url: '',
    token: '',
    newToken: '',
    admin_url: ''
  });

  const [newTech, setNewTech] = useState('');
  const [errors, setErrors] = useState({});
  const [tokenCopied, setTokenCopied] = useState(false);

  // Статусы проектов
  const statuses = [
    { value: 'active', label: 'Активный', color: '#3fb950' },
    { value: 'development', label: 'В разработке', color: '#d29922' },
    { value: 'staging', label: 'Тестирование', color: '#8957e5' },
    { value: 'archived', label: 'Архивирован', color: '#7d8590' }
  ];

  // Загрузка данных проекта
  useEffect(() => {
    document.title = 'Редактирование проекта | DevSpace';
    fetchProject();
  }, [uuid]);

  const fetchProject = async () => {
    setFetchLoading(true);
    try {
      const response = await projectsAPI.get(uuid, false);
      if (response.success && response.data) {
        const project = response.data;
        setFormData({
          name: project.name || '',
          description: project.description || '',
          status: project.status || 'active',
          tech_stack: project.tech_stack || [],
          repository_url: project.repository_url || '',
          token: project.token || '',
          newToken: '',
          admin_url: project.admin_url || ''
        });
      } else {
        setErrors({ submit: 'Проект не найден' });
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setErrors({ submit: 'Ошибка загрузки проекта' });
    } finally {
      setFetchLoading(false);
    }
  };

  // Валидация URL
  const validateUrl = (url) => {
    if (!url) return true;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  // Обработка изменения полей
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Очищаем ошибку для этого поля
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Валидация URL
    if (name === 'repository_url' && value) {
      if (!validateUrl(value)) {
        setErrors(prev => ({ ...prev, repository_url: 'URL должен начинаться с http:// или https://' }));
      } else {
        setErrors(prev => ({ ...prev, repository_url: null }));
      }
    }
  };

  // Добавление технологии
  const handleAddTech = () => {
    if (newTech.trim() && !formData.tech_stack.includes(newTech.trim())) {
      setFormData(prev => ({
        ...prev,
        tech_stack: [...prev.tech_stack, newTech.trim()]
      }));
      setNewTech('');
    }
  };

  // Удаление технологии
  const handleRemoveTech = (tech) => {
    setFormData(prev => ({
      ...prev,
      tech_stack: prev.tech_stack.filter(t => t !== tech)
    }));
  };

  // Обработка нажатия Enter в поле технологии
  const handleTechKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTech();
    }
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Название проекта обязательно';
    } else if (formData.name.length < 1) {
      newErrors.name = 'Название должно содержать минимум 1 символ';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Название не должно превышать 255 символов';
    }

    if (formData.repository_url && !validateUrl(formData.repository_url)) {
      newErrors.repository_url = 'URL должен начинаться с http:// или https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Отправка формы (обновление)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    formData.uuid = uuid
    formData.token = formData.newToken

    setLoading(true);

    try {
      const response = await projectsAPI.patch(formData);
      if (response.success) {
        navigate(`/projects/${uuid}`);
      } else {
        setErrors({ submit: response.message || 'Ошибка при обновлении проекта' });
      }
    } catch (error) {
      console.error('Update error:', error);
      setErrors({ submit: error.message || 'Ошибка сети. Попробуйте позже.' });
    } finally {
      setLoading(false);
    }
  };

  // Копирование сохранённого токена
  const handleCopyToken = async () => {
    const response = await projectsAPI.getToken(uuid);
    if (!response.success || !response.data?.token) {
      setErrors(prev => ({ ...prev, token: response.message || 'Не удалось получить токен' }));
      return;
    }
    try {
      await navigator.clipboard.writeText(response.data.token);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (error) {
      setErrors(prev => ({ ...prev, token: 'Не удалось скопировать в буфер обмена' }));
    }
  };

  // Отмена и возврат назад
  const handleCancel = () => {
    navigate(-1);
  };

  // Показ загрузки
  if (fetchLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingOutlined className={styles.loadingSpinner} />
          <p>Загрузка проекта...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleCancel}>
          <ArrowLeftOutlined /> Назад
        </button>
        <h1 className={styles.title}>Редактирование проекта</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Название проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Название проекта <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Введите название проекта"
            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
            maxLength={255}
          />
          {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
          <span className={styles.hint}>
            {formData.name.length}/255 символов
          </span>
        </div>

        {/* Описание проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Описание проекта</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Опишите цели и задачи проекта"
            className={styles.textarea}
            rows={4}
          />
        </div>

        {/* Статус проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Статус проекта</label>
          <div className={styles.statusGroup}>
            {statuses.map(status => (
              <label
                key={status.value}
                className={`${styles.statusOption} ${
                  formData.status === status.value ? styles.statusOptionActive : ''
                }`}
                style={{
                  borderColor: formData.status === status.value ? status.color : 'transparent'
                }}
              >
                <input
                  type="radio"
                  name="status"
                  value={status.value}
                  checked={formData.status === status.value}
                  onChange={handleChange}
                  className={styles.radioInput}
                />
                <span
                  className={styles.statusDot}
                  style={{ backgroundColor: status.color }}
                />
                <span className={styles.statusLabel}>{status.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Стек технологий */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Стек технологий</label>
          <div className={styles.techInputGroup}>
            <input
              type="text"
              value={newTech}
              onChange={(e) => setNewTech(e.target.value)}
              onKeyPress={handleTechKeyPress}
              placeholder="Добавьте технологию (например, React, Python, Docker)"
              className={styles.techInput}
            />
            <button
              type="button"
              onClick={handleAddTech}
              className={styles.addTechButton}
            >
              <PlusCircleOutlined /> Добавить
            </button>
          </div>

          <div className={styles.techStackList}>
            {formData.tech_stack.map((tech, index) => (
              <div key={index} className={styles.techTag}>
                <span className={styles.techTagText}>{tech}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTech(tech)}
                  className={styles.removeTechButton}
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
            {formData.tech_stack.length === 0 && (
              <div className={styles.emptyTechStack}>
                <PlusOutlined /> Добавьте технологии, которые используете в проекте
              </div>
            )}
          </div>
        </div>

        {/* Токен для подключения */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Токен подключения</label>
          <div className={styles.urlInputGroup}>
            <SecurityScanOutlined className={styles.urlIcon} />
            <input
              type="text"
              name="newToken"
              value={formData.newToken}
              onChange={handleChange}
              placeholder={formData.token ? 'Токен установлен' : 'Токен не установлен'}
              className={`${styles.input} ${errors.token ? styles.inputError : ''}`}
            />
            {formData.token && (
              <button
                type="button"
                onClick={handleCopyToken}
                className={styles.addTechButton}
              >
                {tokenCopied ? <CheckOutlined /> : <CopyOutlined />} {tokenCopied ? 'Скопировано' : 'Копировать'}
              </button>
            )}
          </div>
          {errors.token && (
            <span className={styles.errorMessage}>{errors.token}</span>
          )}
          <span className={styles.hint}>
            Укажите токен подключения к админ панели проекта
          </span>
        </div>

        {/* Ссылка на админку проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Ссылка на админку проекта</label>
          <div className={styles.urlInputGroup}>
            <LinkOutlined className={styles.urlIcon} />
            <input
              type="url"
              name="admin_url"
              value={formData.admin_url}
              onChange={handleChange}
              placeholder="https://some-projects/api/v1/admin/"
              className={`${styles.input} ${errors.admin_url ? styles.inputError : ''}`}
            />
          </div>
          {errors.admin_url && (
            <span className={styles.errorMessage}>{errors.admin_url}</span>
          )}
          <span className={styles.hint}>
            Укажите ссылку на админку проекта
          </span>
        </div>

        {/* Ссылка на репозиторий */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Ссылка на репозиторий</label>
          <div className={styles.urlInputGroup}>
            <GithubOutlined className={styles.urlIcon} />
            <input
              type="url"
              name="repository_url"
              value={formData.repository_url}
              onChange={handleChange}
              placeholder="https://github.com/username/repository"
              className={`${styles.input} ${errors.repository_url ? styles.inputError : ''}`}
            />
          </div>
          {errors.repository_url && (
            <span className={styles.errorMessage}>{errors.repository_url}</span>
          )}
          <span className={styles.hint}>
            Укажите ссылку на GitHub, GitLab или другой репозиторий
          </span>
        </div>

        {/* Ошибка отправки */}
        {errors.submit && (
          <div className={styles.submitError}>
            <CloseOutlined /> {errors.submit}
          </div>
        )}

        {/* Кнопки действий */}
        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.cancelButton}
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? (
              <>Сохранение...</>
            ) : (
              <>
                <SaveOutlined /> Сохранить изменения
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProject;